<template>
  <main class="homepage">
    <Promotions
      class="homepage__promotions homepage__section homepage__section--md"
      :promotions="promotions"
      is-fluid
    />
    <GridContainer>
      <HomepageCategories class="homepage__section homepage__section--md" />
      <ThemeTeasers class="homepage__section homepage__section--md" />
      <GridRow class="homepage__section homepage__section--md">
        <GridColumn>
          <BannerTeaser
            class="coupon-banner"
            image-xs="/content/content-images/homepage/coupon/5181dc35597482bd536410fa453b0ea4/aktionsteaser-s@2x.png"
            image-sm="/content/content-images/homepage/coupon/72a42e08aead662bf703d80f79348685/aktionsteaser-m@2x.png"
            image-md="/content/content-images/homepage/coupon/f1d509e1477d38b02c74ade25985bba7/aktionsteaser-l@2x.png"
            alt="Rabatt-Aktionen - Jetzt Vorteile sichern!"
            lazy
          >
            <template #default>
              <Heading
                class="coupon-banner__heading"
                tag="h2"
                size="md"
                font-weight="ultra"
                spaces-bottom
              >
                Aktionen nutzen und Vorteile sichern!
              </Heading>
              <Heading
                font-weight="regular"
                spaces-bottom
                tag="h3"
                class="coupon-banner__subheading"
              >
                Nutze aktuelle Aktionsangebote und löse Gutscheine hier direkt ein.
              </Heading>
              <link-button
                to="/preisvorteile-sichern"
                primary
                tag="nuxt-link"
                class="btn--transparent"
              >
                Jetzt Vorteile sichern!
              </link-button>
            </template>
          </BannerTeaser>
        </GridColumn>
      </GridRow>
      <HomepageHighlights class="homepage__section" />
      <BrandWallpaper class="homepage__section homepage__section--md" />
      <SustainableProducts class="homepage__section" />
      <RecommendedProducts class="homepage__section" />
      <Services class="homepage__section--md" />
    </GridContainer>
  </main>
</template>

<script lang="ts">
import { useNuxtApp, useLazyAsyncData } from '#imports';
import { defineComponent, computed, ref } from 'vue';

import SustainableProducts from '@/components/homepage/sustainable-products.vue';
import BrandWallpaper from '@/components/brand-wallpaper.vue';
import ThemeTeasers from '@/components/homepage/theme-teasers.vue';
import Services from '@/components/homepage/services.vue';
import HomepageCategories from '@/components/homepage/homepage-categories.vue';
import HomepageHighlights from '@/components/homepage/homepage-highlights.vue';
import Promotions from '@/components/promotions.vue';
import RecommendedProducts from '@/components/recommended-products.vue';

import {
  Heading,
  LinkButton,
  GridContainer,
  GridRow,
  GridColumn,
  BannerTeaser,
} from '@i22-td-smarthome/component-library';
import { Track } from '@/helpers/page-tracking';
import { getHeader } from '@/helpers/page-header-compositon';
import { contentDataFor } from '@/lib/content-data-factory';
import { getActivePromotions, type StageSliderData } from '@/lib/promotions';
import type { ListOffer } from '@/lib/goliath/offers';
import type { PromotionOffer } from '@/lib/offers-by-group';

export default defineComponent({
  name: 'Index',
  components: {
    SustainableProducts,
    BrandWallpaper,
    ThemeTeasers,
    Services,
    HomepageCategories,
    HomepageHighlights,
    Promotions,
    RecommendedProducts,
    Heading,
    LinkButton,
    GridContainer,
    GridRow,
    GridColumn,
    BannerTeaser,
  },
  head: {},
  setup: () => {
    Track({ page_type: 'theme.landing' });
    getHeader({
      title: ref('Smartphones, Tablets und Zubehör'),
      description: ref(
        'Große Auswahl an Handys & Smartphones, Tablets und  Zubehör. Geprüfte Sicherheit ✓ Schnelle Lieferzeiten ✓ Jetzt entdecken und bestellen!'
      ),
    });

    const { $store, $config } = useNuxtApp();

    const dateToday = computed(() =>
      new Date($store.getters['server-date/getServerDate']).getTime()
    );

    const { data: promotions, error: errorData } = useLazyAsyncData<
      PromotionOffer<ListOffer, StageSliderData>[]
    >('homepage-promotions', async () => {
      try {
        const data = await contentDataFor('shared/promotions.json', $config);
        if (!data?.promotions || !Array.isArray(data.promotions)) return;
        // eslint-disable-next-line consistent-return
        return await getActivePromotions(data.promotions, dateToday.value);
      } catch (e: any) {
        console.error(e);
      }
      // eslint-disable-next-line consistent-return
      return [];
    });

    // handle errors
    if (errorData.value) {
      console.error(errorData.value);
    }

    return {
      promotions,
    };
  },
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.homepage {
  &__section {
    margin-bottom: 150px;

    &--md {
      margin-bottom: 90px;
    }
  }
}

.coupon {
  &-banner {
    padding-left: $grid-base;
    height: 540px;
    align-items: flex-end;
    color: var(--color-font-fixed);
    @include mq(500px) {
      height: 600px;
    }

    @include mq($mq-sm) {
      height: 340px;
      padding-left: 2 * $grid-base;
      align-items: center;
      background-position: top 0 right 30%;
    }
    &__heading {
      @include font-size(36px, 36px);
      margin-bottom: 0.5em;

      @include mq($mq-sm) {
        text-transform: uppercase;
      }
    }

    &__subheading {
      @include font-size(18px, 24px);
    }
  }
}
</style>
